import { gql } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import { AutoDataTable } from "../../../components/AutoDataTable";
import { CreationForm } from "../../../components/form/CreationForm";
import { Text, Group } from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import * as yup from "yup";
import { MetadataEditor } from "../components/MetadataEditor";
import { ListPageHeader } from "../../../components/PageHeader";
import { StatusBadge } from "../../../components/StatusBadge";
import { useOrganizationLimits } from "../../../../../business/limits.hook";
import { LimitBadge } from "../../../components/LimitIndicator";

const CURRENCIES_QUERY = gql`
  query ($cursor: String, $count: Float) {
    currencies(page: { first: $count, after: $cursor }) {
      nodes {
        id
        name
        symbol
        description
        hidden
        externalId
        createdAt
      }
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

const DELETE_CURRENCY_MUTATION = gql`
  mutation ($id: ID!) {
    currencyDelete(id: $id) {
      id
    }
  }
`;

const CREATE_CURRENCY_MUTATION = gql`
  mutation ($input: CurrencyCreateInput!) {
    currencyCreate(input: $input) {
      id
      name
      symbol
      description
      hidden
      externalId
    }
  }
`;

const UPDATE_CURRENCY_MUTATION = gql`
  mutation ($id: ID!, $input: CurrencyUpdateInput!) {
    currencyUpdate(id: $id, input: $input) {
      id
      name
      symbol
      description
      hidden
      externalId
    }
  }
`;

export function CreateCurrencyForm({ onSuccess }: { onSuccess?: () => void }) {
  const [createCurrency] = useMutation(CREATE_CURRENCY_MUTATION, {
    refetchQueries: ["currencies"],
  });

  return (
    <CreationForm
      title="Create Currency"
      schemaDefinition={{
        name: {
          type: "INPUT",
          label: "Name",
          yupConfig: yup.string().required(),
          inputType: "text",
          placeholder: "Enter currency name",
        },
        symbol: {
          type: "INPUT",
          label: "Symbol",
          yupConfig: yup.string().required().max(10),
          inputType: "text",
          placeholder: "e.g., $",
        },
        externalId: {
          type: "INPUT",
          label: "External ID",
          yupConfig: yup.string().optional().nullable(),
          inputType: "text",
          placeholder: "Enter external ID (optional)",
        },
        description: {
          type: "INPUT",
          label: "Description",
          yupConfig: yup.string().required(),
          inputType: "text",
          placeholder: "Describe what this currency is used for",
        },
        hidden: {
          type: "CHECKBOX",
          label: "Hidden",
          yupConfig: yup.boolean(),
          defaultValue: false,
        },
        metadata: {
          type: "DYNAMIC",
          label: "Metadata",
          yupConfig: yup
            .array()
            .of(
              yup.object().shape({
                property: yup.string().required(),
                value: yup.string().required(),
              }),
            )
            .required(),
          defaultValue: [],
          element: (onChange: (value: any) => void) => (
            <MetadataEditor onChange={onChange} value={[]} />
          ),
        },
      }}
      onSubmit={async (values: any) => {
        await createCurrency({
          variables: {
            input: {
              name: values.name,
              symbol: values.symbol,
              description: values.description,
              hidden: values.hidden,
              externalId: values.externalId || undefined,
              metadata: values.metadata || [],
            },
          },
        });
        notifications.show({
          title: "Currency created",
          message: "The currency has been created successfully",
          color: "green",
          autoClose: 3000,
        });
        onSuccess?.();
      }}
    />
  );
}

export function EditCurrencyForm({
  currencyId,
  onClose,
}: {
  currencyId: string;
  onClose: () => void;
}) {
  const [updateCurrency] = useMutation(UPDATE_CURRENCY_MUTATION, {
    refetchQueries: ["currencies"],
  });
  const { data, loading } = useQuery<any>(
    gql`
      query ($id: ID!) {
        currencies(filter: { ids: [$id] }, page: { first: 1 }) {
          nodes {
            id
            name
            symbol
            description
            hidden
            externalId
            metadata {
              property
              value
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    `,
    {
      variables: { id: currencyId },
      skip: !currencyId,
    },
  );

  if (loading) return <div>Loading...</div>;

  const currency = data?.currencies?.nodes?.[0];

  return (
    <CreationForm
      title="Edit Currency"
      schemaDefinition={{
        name: {
          type: "INPUT",
          label: "Name",
          yupConfig: yup.string().required(),
          inputType: "text",
          placeholder: "Enter currency name",
          defaultValue: currency?.name,
        },
        symbol: {
          type: "INPUT",
          label: "Symbol",
          yupConfig: yup.string().required().max(10),
          inputType: "text",
          placeholder: "e.g., $",
          defaultValue: currency?.symbol,
        },
        externalId: {
          type: "INPUT",
          label: "External ID",
          yupConfig: yup.string().optional().nullable(),
          inputType: "text",
          placeholder: "Enter external ID (optional)",
          defaultValue: currency?.externalId,
        },
        description: {
          type: "INPUT",
          label: "Description",
          yupConfig: yup.string().required(),
          inputType: "text",
          placeholder: "Describe what this currency is used for",
          defaultValue: currency?.description,
        },
        hidden: {
          type: "CHECKBOX",
          label: "Hidden",
          yupConfig: yup.boolean(),
          defaultValue: currency?.hidden,
        },
        metadata: {
          type: "DYNAMIC",
          label: "Metadata",
          yupConfig: yup
            .array()
            .of(
              yup.object().shape({
                property: yup.string().required(),
                value: yup.string().required(),
              }),
            )
            .required(),
          defaultValue: currency?.metadata || [],
          element: (onChange: (value: any) => void) => (
            <MetadataEditor
              onChange={onChange}
              value={currency?.metadata || []}
            />
          ),
        },
      }}
      onSubmit={async (values: any) => {
        await updateCurrency({
          variables: {
            id: currencyId,
            input: {
              name: values.name,
              symbol: values.symbol,
              description: values.description,
              hidden: values.hidden,
              externalId: values.externalId || undefined,
              metadata: values.metadata || [],
            },
          },
        });
        notifications.show({
          title: "Currency updated",
          message: "The currency has been updated successfully",
          color: "green",
          autoClose: 3000,
        });
        onClose();
      }}
    />
  );
}

export function CurrenciesList() {
  const handleEditClick = (item: any) => {
    const modalId = `edit-currency-${item.id}`;
    modals.open({
      modalId,
      title: "Edit Currency",
      size: "lg",
      children: (
        <EditCurrencyForm
          currencyId={item.id}
          onClose={() => modals.close(modalId)}
        />
      ),
    });
  };

  return (
    <AutoDataTable
      itemPath="currencies"
      createButtonText={false}
      query={CURRENCIES_QUERY}
      deleteMutation={DELETE_CURRENCY_MUTATION}
      linkClick={handleEditClick}
      columns={[
        {
          accessor: "name",
          title: "Name",
        },
        {
          accessor: "symbol",
          title: "Symbol",
        },
        {
          accessor: "externalId",
          title: "External ID",
          rawElement: (record: any) => (
            <Text size="sm" c="dimmed">
              {record.externalId || "-"}
            </Text>
          ),
        },
        {
          accessor: "description",
          title: "Description",
        },
        {
          accessor: "hidden",
          title: "Status",
          rawElement: (record: any) => (
            <StatusBadge
              status={record.hidden ? "inactive" : "active"}
              label={record.hidden ? "Hidden" : "Visible"}
            />
          ),
        },
      ]}
    />
  );
}

const openCreateCurrencyModal = () => {
  const modalId = "create-currency";
  modals.open({
    modalId,
    title: "Create Currency",
    size: "lg",
    children: (
      <CreateCurrencyForm onSuccess={() => modals.close(modalId)} />
    ),
  });
};

export function Currencies() {
  const { getLimit } = useOrganizationLimits(["CURRENCIES"]);
  const currenciesLimit = getLimit("CURRENCIES");

  return (
    <div className="wp-fade-in">
      <ListPageHeader
        title="Currencies"
        subtitle="Virtual currencies for rewards, purchases, and player progression"
        createLabel="Create Currency"
        onCreate={openCreateCurrencyModal}
        showBreadcrumbs={false}
      />
      {currenciesLimit && (
        <Group mb="md">
          <LimitBadge limit={currenciesLimit} label="Currencies limit" />
        </Group>
      )}
      <CurrenciesList />
    </div>
  );
}
